import React from 'react';
import { useTranslations } from '../../localization/useTranslations';
import { PaymentSummaryProps } from '../types/Payment';
import { getLocaleCurrencyInfo } from '../../utils/getCurrencyInfo';

const PaymentSummary: React.FC<PaymentSummaryProps> = ({
  name,
  code,
  amount,
  next_delivery,
  email,
  quantity,
  gift_address,
  gift_email,
  invoice_url,
  length,
  failed,
  products,
  payment_gateway,
}) => {
  const translations = useTranslations();

  const invoice_name = 'invoice-' + invoice_url.split('/').pop() + '.PDF';
  const { symbol: currencySymbol } = getLocaleCurrencyInfo();

  // remove next delivery date for failed payments in SK - on request
  const shouldExcludeNextDeliveryDate = window.country.toLowerCase() === 'sk' && failed;

  return (
    <section>
      <div className="section section--overview">
        <h4 className="section__title">{translations.paymentSummary?.summaryTitle || 'Order overview'}</h4>

        {products && products.length > 1 ? (
          <>
            {products.map((product, index) => (
              <div key={index} className="section__wrapper h-mb--30">
                <div className="section__details">
                  <div className="section__name link-16">
                    {product.count} x {product.name}
                  </div>
                  <span className="section__price link-16">
                    {product.amount} {currencySymbol}
                  </span>
                </div>
              </div>
            ))}
            <div className="section__wrapper h-mb--30">
              <div className="section__details">
                <div className="section__name link-16">
                  {translations.paymentSummary?.summaryOrderTotal || 'Order total'}
                </div>
                <span className="section__price link-16">
                  {amount} {currencySymbol}
                </span>
              </div>
            </div>
          </>
        ) : (
          <div className="section__wrapper">
            <div className="section__details">
              <div className="section__name link-16">
                {quantity} x {name}
              </div>

              {['all-you-can-read', 'classic', 'R1D2022', 'R2D2022'].includes(code) &&
                !shouldExcludeNextDeliveryDate && (
                  <span className="section__date">
                    {translations.paymentSummary?.summaryNextDeliveryDate || 'Next delivery date:'} {next_delivery}
                  </span>
                )}

              {['all-you-can-read', 'premium', 'premium roční'].includes(code) && (
                <span className="section__email">{email}</span>
              )}
            </div>

            {length === 365 ? (
              <div className="section__price link-16">
                {amount} {currencySymbol} / {translations.paymentSummary?.summaryYear || 'year'}
              </div>
            ) : length === 730 ? (
              <div className="section__price link-16">
                {amount} {currencySymbol} / {translations.paymentSummary?.summaryTwoYears || '2 years'}
              </div>
            ) : (
              <div className="section__price link-16">
                {amount} {currencySymbol} / {translations.paymentSummary?.summaryMonth || 'month'}
              </div>
            )}
          </div>
        )}
      </div>
      {invoice_url && (
        <div className="section section--invoice">
          <div className="section__label">{translations.paymentSummary?.summaryIvoice || 'Invoice'}</div>
          <h5>
            <a className="section__link" href={invoice_url} rel="noopener noreferrer" target="_blank">
              {invoice_name}
            </a>
          </h5>
        </div>
      )}
      {!failed &&
        payment_gateway !== 'bank_transfer' &&
        ['all-you-can-read', 'classic', 'R1D2022', 'R2D2022'].includes(code) && (
          <div className="section section--next-delivery">
            <h4 className="section__title">
              {translations.paymentSummary?.summaryNextDeliveryTitle || 'Next delivery'}
            </h4>

            <div className="section__wrapper">
              <div className="section__details">
                <div className="section__description">
                  {translations.paymentSummary?.summaryNextDeliveryTextBeforeDate || 'We will send your first issue on'}{' '}
                  <b>
                    {next_delivery}
                    {', '}
                  </b>
                  {translations.paymentSummary?.summaryNextDeliveryTextAfterDate ||
                    'you already have an online version of the magazine available. Just go to your Account, where in the Subscriptions section you will find a link to articles from the magazine, and you will also find it in the menu on the Forbes.sk website. The archive is available from June 2024.'}
                </div>
              </div>
            </div>
          </div>
        )}
      {typeof gift_address === 'object' && gift_address !== null && (
        <div className="section section--gift-address">
          <h4 className="section__title">{translations.paymentSummary?.summaryGiftAddress || 'Gift address'}</h4>

          <div className="section__wrapper">
            <div className="section__details">
              <div className="section__description">
                {gift_address.first_name} {gift_address.last_name}
                <br />
                {gift_address.address} {gift_address?.number}, <br />
                {gift_address.zip} {gift_address.city} <br />
                {gift_address.country}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TODO: Add gift codes, when they are ready */}
      {['all-you-can-read', 'premium', 'premium roční'].includes(code) && gift_email && (
        <div className="section section--overview">
          <h4 className="section__title">{translations.paymentSummary?.summaryGiftEmail || 'Gift email'}</h4>

          <div className="section__wrapper">
            <div className="section__details">
              <div className="section__description">{gift_email}</div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default PaymentSummary;
